import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import {
  addAppLaunchAlert,
  getAppLaunchAlerts,
  installApp,
  removeAppLaunchAlert,
  uninstallApp,
} from "../api/mintSpace";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import {
  APP_STORE_ORDER,
  APP_STORE_PAGES,
  CATEGORY_KEYS,
  SINGLE_COPY_APP_IDS,
  SUGGESTION_ORDER,
} from "../data/appStorePages";
import {
  COURSES_APP_ID,
  getInstanceDisplayName,
  getInstanceVisibility,
  instanceNavId,
  mintSpaceAppIdToNav,
  normalizeInstalledApps,
} from "../utils/mintSpaceApps";
import AddAppModal from "../components/appStore/AddAppModal";
import AppStoreAppPage from "../components/appStore/AppStoreAppPage";
import ManageAppModal from "../components/appStore/ManageAppModal";
import AppCover from "../components/ds/AppCover";
import AppIcon, { APP_COLORS, appKey } from "../components/ds/AppIcon";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";

const FILTERS = [
  { id: "all", labelKey: "appStorePage.filterAll" },
  { id: "sell", labelKey: "appStorePage.filterSell" },
  { id: "teach", labelKey: "appStorePage.filterTeach" },
  { id: "grow", labelKey: "appStorePage.filterGrow" },
];

/** Figma "Your space menu": what members find in the sidebar, and room for the next app. */
const SpaceMenuCard = ({ t, mintSpace, spaceName, copies, appsById, onOpen }) => (
  <section className="flex min-w-0 flex-col gap-space-4 rounded-radius-lg bg-bg-raised p-space-20">
    <div className="mb-space-8 flex flex-col gap-space-4">
      <h2 className="text-headline text-label-primary">{t("appStorePage.menuTitle")}</h2>
      <p className="text-footnote text-label-tertiary">{t("appStorePage.menuSubtitle", { space: spaceName })}</p>
    </div>
    {copies.map((copy) => {
      const app = appsById[copy.appId];
      if (!app) return null;
      return (
        <button
          key={copy.instanceId}
          type="button"
          onClick={() => onOpen(copy)}
          className="flex items-center gap-space-12 rounded-[12px] p-space-8 text-left transition-colors hover:bg-fill-quaternary"
        >
          <AppIcon app={app.id} size={32} />
          <span className="min-w-0 flex-1 truncate text-subheadline-semibold text-label-primary">
            {getInstanceDisplayName(mintSpace, copy, app.storeName)}
          </span>
          {getInstanceVisibility(mintSpace, copy) === "private" && (
            <Icon name="lock" size={14} className="text-label-tertiary" />
          )}
        </button>
      );
    })}
    <div className="flex items-center gap-space-12 rounded-[12px] p-space-8 shadow-[inset_0_0_0_1px_var(--color-separator-opaque)]">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-fill-quaternary text-label-tertiary">
        <Icon name="plus" size={16} />
      </span>
      <span className="min-w-0 flex-1 text-subheadline text-label-tertiary">{t("appStorePage.nextSlot")}</span>
    </div>
  </section>
);

/** Figma "Suggested": up to three apps this space hasn't added, and why. */
const SuggestedCard = ({ t, spaceName, apps, sellsCourses, onAdd, onShow }) => (
  <section className="flex min-w-0 flex-col rounded-radius-lg bg-bg-raised p-space-20">
    <div className="flex flex-col gap-space-4 pb-space-8">
      <h2 className="text-headline text-label-primary">{t("appStorePage.suggestedTitle", { space: spaceName })}</h2>
      <p className="text-footnote text-label-tertiary">{t("appStorePage.suggestedSubtitle")}</p>
    </div>
    <div className="divide-y divide-separator-default">
      {apps.map((app) => {
        const key = `appStorePage.apps.${app.id}`;
        const generic = !sellsCourses && (app.id === "consulting" || app.id === "mintly-cloud");
        return (
          <div key={app.id} data-app={appKey(app.id)} className="relative flex items-center gap-[14px] py-space-12">
            <button
              type="button"
              aria-label={app.storeName}
              onClick={() => onShow(app)}
              className="absolute inset-0"
            />
            <AppIcon app={app.id} size={44} />
            <div className="flex min-w-0 flex-1 flex-col gap-space-4">
              <h3 className="truncate text-headline text-label-primary">{app.storeName}</h3>
              <p className="text-footnote text-label-secondary">{t(`${key}.${generic ? "whyGeneric" : "why"}`)}</p>
            </div>
            <Button variant="tinted" size="s" className="relative" onClick={() => onAdd(app)}>
              {t("appStorePage.add")}
            </Button>
          </div>
        );
      })}
    </div>
  </section>
);

const AppCard = ({ t, app, copies, notified, busy, onShow, onAdd, onOpen, onToggleNotify }) => {
  const page = APP_STORE_PAGES[app.id];
  const builtIn = app.isDefault && copies > 0;
  const singleCopy = SINGLE_COPY_APP_IDS.includes(app.id);

  const meta = app.comingSoon
    ? t("appStorePage.soon")
    : builtIn
      ? t("appStorePage.builtIn")
      : copies > 0
        ? t("appStorePage.inYourSpace", { count: copies })
        : t(`appStorePage.${page.meetCategory ? "catTeachMeet" : CATEGORY_KEYS[page.category]}`);

  const action = app.comingSoon ? (
    <Button
      variant="gray"
      size="s"
      loading={busy}
      icon={notified ? <Icon name="check" size={16} /> : null}
      className="relative"
      onClick={onToggleNotify}
    >
      {t(notified ? "appStorePage.notified" : "appStorePage.notify")}
    </Button>
  ) : builtIn || (singleCopy && copies > 0) ? (
    <Button variant="gray" size="s" className="relative" onClick={onOpen}>
      {t("appStorePage.open")}
    </Button>
  ) : (
    <Button variant="tinted" size="s" className="relative" onClick={onAdd}>
      {t(copies > 0 ? "appStorePage.addAnother" : "appStorePage.add")}
    </Button>
  );

  return (
    <article data-app={appKey(app.id)} className="relative flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
      <button type="button" aria-label={app.storeName} onClick={onShow} className="absolute inset-0" />
      <div className="pointer-events-none relative h-[96px] shrink-0">
        <AppCover app={app.id} />
      </div>
      <div className="flex flex-1 flex-col gap-space-12 p-space-20">
        <div className="flex items-center gap-space-12">
          <AppIcon app={app.id} size={44} />
          <div className="flex min-w-0 flex-1 flex-col gap-space-4">
            <h3 className="text-headline text-label-primary">{app.storeName}</h3>
            <p className="text-footnote text-label-tertiary">{meta}</p>
          </div>
          {action}
        </div>
        <p className="text-subheadline text-label-secondary">{t(`appStorePage.apps.${app.id}.description`)}</p>
        {page.sells && (
          <span className="mt-auto inline-flex items-center gap-space-8 text-caption-1-semibold text-label-secondary">
            <span className={`size-[6px] shrink-0 rounded-full ${APP_COLORS[appKey(app.id)].base}`} />
            {t("appStorePage.sellsAsProduct")}
          </span>
        )}
      </div>
    </article>
  );
};

const MintSpaceAppStorePage = ({
  mintSpaceId,
  isDark,
  setToast,
  mintSpace,
  onAppChange,
  onOpenApp,
  onOpenProperties,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rootRef = useRef(null);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [alerts, setAlerts] = useState([]);
  const [alertBusy, setAlertBusy] = useState(null);
  /** The app in the add popup, and the copy once it's added. */
  const [adding, setAdding] = useState(null);
  const [addedCopy, setAddedCopy] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [managing, setManaging] = useState(null);
  const [removing, setRemoving] = useState(false);

  const apps = useMemo(() => {
    const catalog = getMintSpaceAppStoreCatalog(t);
    return APP_STORE_ORDER.map((id) => {
      const entry = catalog.find((app) => app.id === id);
      return { ...entry, storeName: t(`appStorePage.apps.${id}.name`) };
    });
  }, [t]);
  const appsById = useMemo(() => Object.fromEntries(apps.map((app) => [app.id, app])), [apps]);

  const instances = useMemo(
    () => normalizeInstalledApps(mintSpace?.installedApps, mintSpace),
    [mintSpace]
  );
  const copiesOf = useCallback((id) => instances.filter((copy) => copy.appId === id), [instances]);
  const spaceName = mintSpace?.name || "";

  useEffect(() => {
    getAppLaunchAlerts().then(setAlerts).catch(() => {});
  }, []);

  const showingId = searchParams.get("app");
  const showing = appsById[showingId] || null;

  const showApp = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("app", id);
      else next.delete("app");
      return next;
    });

  useEffect(() => {
    rootRef.current?.closest(".campaign-window-scrollbar")?.scrollTo(0, 0);
  }, [showingId]);

  const openCopy = (copy) => onOpenApp?.(instanceNavId(copy));
  const openApp = (app) => {
    const [copy] = copiesOf(app.id);
    if (copy && !app.isDefault) openCopy(copy);
    else onOpenApp?.(mintSpaceAppIdToNav(app.id));
  };

  const startAdding = (app) => {
    setAddedCopy(null);
    setAdding(app);
  };

  const confirmAdd = async (visibility) => {
    if (!adding) return;
    const known = new Set(instances.map((copy) => copy.instanceId));
    setAddLoading(true);
    try {
      const result = await installApp(mintSpaceId, adding.id, visibility);
      if (result.success) {
        onAppChange?.(result.data);
        const after = normalizeInstalledApps(result.data.installedApps, result.data);
        setAddedCopy(after.find((copy) => !known.has(copy.instanceId)) || after[after.length - 1]);
      }
    } catch (error) {
      setToast?.(error.message || t("mintSpace.appAddFailed"));
    } finally {
      setAddLoading(false);
    }
  };

  const closeAdding = () => {
    setAdding(null);
    setAddedCopy(null);
  };

  const removeCopy = async (copy) => {
    setRemoving(true);
    try {
      const result = await uninstallApp(mintSpaceId, copy.appId, copy.instanceId);
      if (result.success) {
        setToast?.(t("mintSpace.appRemoved"));
        onAppChange?.(result.data);
        if (copiesOf(copy.appId).length <= 1) setManaging(null);
      }
    } catch (error) {
      setToast?.(error.message || t("mintSpace.appRemoveFailed"));
    } finally {
      setRemoving(false);
    }
  };

  const toggleNotify = async (app) => {
    setAlertBusy(app.id);
    try {
      const change = alerts.includes(app.id) ? removeAppLaunchAlert : addAppLaunchAlert;
      setAlerts(await change(app.id));
    } catch (error) {
      setToast?.(error?.response?.data?.message || t("toasts.error"));
    } finally {
      setAlertBusy(null);
    }
  };

  const filteredApps = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((app) => {
      if (filter !== "all" && APP_STORE_PAGES[app.id].category !== filter) return false;
      if (!q) return true;
      return (
        app.storeName.toLowerCase().includes(q) ||
        t(`appStorePage.apps.${app.id}.description`).toLowerCase().includes(q)
      );
    });
  }, [apps, filter, query, t]);

  const suggestions = SUGGESTION_ORDER.filter((id) => copiesOf(id).length === 0)
    .slice(0, 3)
    .map((id) => appsById[id]);

  const popups = (
    <>
      <AddAppModal
        app={adding}
        spaceName={spaceName}
        added={addedCopy}
        loading={addLoading}
        isDark={isDark}
        onConfirm={confirmAdd}
        onOpenAdded={() => {
          const copy = addedCopy;
          closeAdding();
          openCopy(copy);
        }}
        onClose={closeAdding}
      />
      <ManageAppModal
        app={managing}
        copies={managing ? copiesOf(managing.id) : []}
        mintSpace={mintSpace}
        isDark={isDark}
        removing={removing}
        onOpen={(navId) => {
          setManaging(null);
          onOpenApp?.(navId);
        }}
        onSettings={(copy) => {
          setManaging(null);
          onOpenProperties?.({ instanceId: copy.instanceId, catalogAppId: copy.appId });
        }}
        onRemove={removeCopy}
        onClose={() => setManaging(null)}
      />
    </>
  );

  return (
    <div
      ref={rootRef}
      className="px-space-8 pb-space-24 pt-space-12 font-ds min-[600px]:px-space-12 lg:px-space-24 lg:pt-space-16"
    >
      {showing ? (
        <AppStoreAppPage
          app={showing}
          copies={copiesOf(showing.id).length}
          notified={alerts.includes(showing.id)}
          spaceName={spaceName}
          busy={alertBusy === showing.id}
          onBack={() => showApp(null)}
          onAdd={() => startAdding(showing)}
          onOpen={() => openApp(showing)}
          onManage={() => setManaging(showing)}
          onToggleNotify={() => toggleNotify(showing)}
        />
      ) : (
        <div className="flex flex-col gap-space-24 lg:gap-space-32">
          <div className="flex flex-col gap-space-16 lg:flex-row lg:items-end">
            <div className="flex min-w-0 flex-1 flex-col gap-space-4">
              <h1 className="text-large-title text-label-primary">{t("appStorePage.title")}</h1>
              <p className="text-body text-label-secondary">{t("appStorePage.subtitle")}</p>
            </div>
            <SearchField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("appStorePage.search")}
              className="lg:!w-[300px]"
            />
          </div>

          <SegmentedControl
            track="material"
            label={t("appStorePage.title")}
            value={filter}
            onChange={setFilter}
            className="[&>button]:px-space-8 max-[599px]:[&>button]:flex-auto lg:max-w-[420px]"
            options={FILTERS.map((option) => ({ value: option.id, label: t(option.labelKey) }))}
          />

          {filter === "all" && !query && (
            <div
              className={`grid grid-cols-1 gap-space-16 min-[600px]:gap-space-20 lg:gap-space-24 ${
                suggestions.length > 0 ? "xl:grid-cols-[380px_1fr] min-[1700px]:grid-cols-2" : ""
              }`}
            >
              <SpaceMenuCard
                t={t}
                mintSpace={mintSpace}
                spaceName={spaceName}
                copies={[...instances].reverse()}
                appsById={appsById}
                onOpen={openCopy}
              />
              {suggestions.length > 0 && (
                <SuggestedCard
                  t={t}
                  spaceName={spaceName}
                  apps={suggestions}
                  sellsCourses={copiesOf(COURSES_APP_ID).length > 0}
                  onAdd={startAdding}
                  onShow={(app) => showApp(app.id)}
                />
              )}
            </div>
          )}

          <section className="flex flex-col gap-space-24 lg:gap-space-32">
            <div className="flex items-center gap-space-8">
              <h2 className="min-w-0 flex-1 text-title-2 text-label-primary">{t("appStorePage.allApps")}</h2>
              <span className="text-footnote text-label-tertiary">
                {t("appStorePage.appCount", { count: filteredApps.length })}
              </span>
            </div>

            {filteredApps.length === 0 ? (
              <EmptyState icon="apps" title={t("appStorePage.empty")} message={t("appStorePage.emptyHint")} />
            ) : (
              <div className="grid grid-cols-1 gap-x-space-20 gap-y-space-16 min-[600px]:grid-cols-2 lg:gap-x-space-24 xl:grid-cols-3">
                {filteredApps.map((app) => (
                  <AppCard
                    key={app.id}
                    t={t}
                    app={app}
                    copies={copiesOf(app.id).length}
                    notified={alerts.includes(app.id)}
                    busy={alertBusy === app.id}
                    onShow={() => showApp(app.id)}
                    onAdd={() => startAdding(app)}
                    onOpen={() => openApp(app)}
                    onToggleNotify={() => toggleNotify(app)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
      {popups}
    </div>
  );
};

export default MintSpaceAppStorePage;
